import { DrawerSection } from "@/widgets/editor";
import { UIKit } from "@/shared/ui";
import type { DrawerButtonContext, EditorToolRailItem } from "./types";

type ToggleProps = { disabled?: boolean; isEnabled: boolean; onClick: () => void };
type ClickProps = { onClick: () => void };

const menuDrawerButton = (
	context: DrawerButtonContext,
	definition: Omit<EditorToolRailItem, "active" | "onClick" | "drawer"> & {
		disabled?: boolean;
		drawer: Exclude<DrawerSection, null>;
	},
): EditorToolRailItem => ({
	...definition,
	active: context.drawer === definition.drawer,
	disabled: definition.disabled,
	onClick: () => context.onToggleDrawer(definition.drawer),
});

export const MenuSelectButton = ({ isEnabled, onClick }: ToggleProps): EditorToolRailItem => ({
	label: "Выбор",
	icon: <UIKit.IconCursor />,
	tooltip: "Выделение (V)",
	active: isEnabled,
	onClick,
});

export const MenuDimensionsButton = ({ isEnabled, onClick }: ToggleProps): EditorToolRailItem => ({
	label: "Размеры",
	icon: <UIKit.IconDimensions />,
	tooltip: "Размеры",
	active: isEnabled,
	onClick,
});

export const MenuSelectProjectButton = ({ onClick }: ClickProps): EditorToolRailItem => ({
	label: "Выбор проекта",
	icon: <UIKit.IconDemo />,
	tooltip: "Выбор проекта",
	onClick,
});

export const MenuAreasButton = ({ isEnabled, onClick }: ToggleProps): EditorToolRailItem => ({
	label: "Площади",
	icon: <UIKit.IconArea />,
	tooltip: "Площади",
	active: isEnabled,
	onClick,
});

export const MenuDisabledAreasButton = (): EditorToolRailItem => ({
	label: "Площади",
	icon: <UIKit.IconArea />,
	tooltip: "Площади",
	disabled: true,
	onClick: () => {},
});

export const MenuWallsButton = (context: DrawerButtonContext) =>
	menuDrawerButton(context, {
		drawer: DrawerSection.Walls,
		label: "Стены",
		icon: <UIKit.IconWall />,
		tooltip: "Инструменты стен",
		disabled: context.isReadOnly,
	});
export const MenuOpeningsButton = (context: DrawerButtonContext) =>
	menuDrawerButton(context, {
		drawer: DrawerSection.Openings,
		label: "Проёмы",
		icon: <UIKit.IconDoor />,
		tooltip: "Двери и окна",
		disabled: context.isReadOnly,
	});
export const MenuWiringButton = (context: DrawerButtonContext) =>
	menuDrawerButton(context, {
		drawer: DrawerSection.Wiring,
		label: "Кабели",
		icon: <UIKit.IconCable />,
		tooltip: "Проводка",
		disabled: context.isReadOnly,
	});
export const MenuWaterButton = (context: DrawerButtonContext) =>
	menuDrawerButton(context, {
		drawer: DrawerSection.Water,
		label: "Вода",
		icon: <UIKit.IconPipe />,
		tooltip: "Водоснабжение",
		disabled: context.isReadOnly,
	});
export const MenuLayersButton = (context: DrawerButtonContext) =>
	menuDrawerButton(context, {
		drawer: DrawerSection.Layers,
		label: "Скрыть",
		icon: <UIKit.IconEye />,
		tooltip: "Слои и их видимость",
	});
export const MenuProjectButton = (context: DrawerButtonContext) =>
	menuDrawerButton(context, {
		drawer: DrawerSection.Project,
		label: "Инфо",
		icon: <UIKit.IconProperties />,
		tooltip: "Проект и помещения",
	});
export const MenuFinishesButton = (context: DrawerButtonContext) =>
	menuDrawerButton(context, {
		drawer: DrawerSection.Finishes,
		label: "Отделка",
		icon: <UIKit.IconPaintRoller />,
		tooltip: "Отделка стен и пола, обивка мебели",
		disabled: context.isReadOnly,
	});
export const MenuObjectLayersButton = (context: DrawerButtonContext) =>
	menuDrawerButton(context, {
		drawer: DrawerSection.Fixtures,
		label: "Каталог",
		// Значок кресла: каталог ищут как «где мебель»
		icon: <UIKit.IconFurniture />,
		tooltip: "Каталог: мебель, техника, сантехника, электрика, свет, пол и потолок",
		disabled: context.isReadOnly,
	});

export const MenuCutawayButton = ({ isEnabled, onClick }: ToggleProps): EditorToolRailItem => ({
	label: "Вид внутрь",
	icon: <UIKit.IconCutaway />,
	tooltip: "Вид внутрь",
	active: isEnabled,
	onClick,
});
export const MenuRoomMapButton = ({ isEnabled, onClick }: ToggleProps): EditorToolRailItem => ({
	label: "Карта помещений",
	icon: <UIKit.IconRoomMap />,
	tooltip: "Карта помещений",
	active: isEnabled,
	onClick,
});
export const MenuSunControlButton = ({ isEnabled, onClick }: ToggleProps): EditorToolRailItem => ({
	label: "Солнце",
	icon: <UIKit.IconSun />,
	tooltip: "Настройка солнца",
	active: isEnabled,
	onClick,
});
export const MenuCameraMovePadButton = ({
	isEnabled,
	onClick,
}: ToggleProps): EditorToolRailItem => ({
	label: "Стрелки",
	icon: <UIKit.IconMove />,
	tooltip: "Стрелки управления",
	active: isEnabled,
	onClick,
});
export const MenuFirstPersonButton = ({ isEnabled, onClick }: ToggleProps): EditorToolRailItem => ({
	label: "Прогулка от 1-го лица",
	icon: <UIKit.IconPerson />,
	tooltip: "Прогулка от 1-го лица",
	active: isEnabled,
	onClick,
});
export const MenuShadowsButton = ({ isEnabled, onClick }: ToggleProps): EditorToolRailItem => ({
	label: "Тени",
	icon: <UIKit.IconSun />,
	tooltip: "Тени",
	active: isEnabled,
	onClick,
});
export const MenuSourceLightsButton = ({
	isEnabled,
	onClick,
}: ToggleProps): EditorToolRailItem => ({
	label: "Свет",
	icon: <UIKit.IconLamp />,
	tooltip: "Светильники",
	active: isEnabled,
	onClick,
});
export const MenuGridButton = ({ isEnabled, onClick }: ToggleProps): EditorToolRailItem => ({
	label: "Сетка",
	icon: <UIKit.IconGrid />,
	tooltip: "Сетка",
	active: isEnabled,
	onClick,
});
export const MenuResetViewButton = ({ onClick }: ClickProps): EditorToolRailItem => ({
	label: "Сбросить вид",
	icon: <UIKit.IconReset />,
	tooltip: "Сбросить вид",
	onClick,
});
